/**
 * The welcome video recorder's pure parts: talking points drafted from the
 * intake, how long they take to say, the recording format and the frame the
 * client actually sees.
 */
import type { IntakeEstimate } from "@/features/foreignNationalOutgoingIntakes/intakeEstimate";

/** Recording stops on its own here. */
export const MAX_RECORDING_SECONDS = 180;
/** The progress bar marks this; the welcome screen works best under it. */
export const TARGET_SECONDS = 60;
export const COUNTDOWN_SECONDS = 3;
/**
 * Width over height of the video the client sees. The welcome screen covers
 * its video panel (object-fit: cover); the desktop panel is the narrowest at
 * about 0.88, so what fits this box survives on every screen.
 */
export const CLIENT_CROP_ASPECT = 0.88;
/** Below this peak level (0 to 1) the take is flagged as quiet. */
export const QUIET_PEAK = 0.04;

const WORDS_PER_SECOND = 2.5;

export type TalkingPointsInput = {
  clientName: string | null;
  attorneyName: string | null;
  firmName: string | null;
  title: string;
  estimate: Pick<IntakeEstimate, "questions" | "uploads" | "minutes">;
  due: Date | null;
};

const firstWord = (value: string | null): string =>
  (value ?? "").trim().split(/\s+/)[0] ?? "";

const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

/** "October 29". */
const spokenDate = (date: Date): string =>
  date.toLocaleDateString("en-US", { month: "long", day: "numeric" });

/** One talking point per line, in the order they're said. */
export const draftTalkingPoints = (input: TalkingPointsInput): string[] => {
  const client = firstWord(input.clientName);
  const attorney = firstWord(input.attorneyName);
  const firm = input.firmName?.trim() ?? "";
  const title = input.title.trim();
  const { questions, uploads, minutes } = input.estimate;

  const intro = attorney
    ? `I'm ${attorney}${firm ? ` from ${firm}` : ""}.`
    : firm
    ? `this is ${firm}.`
    : "";
  const size = [
    questions ? plural(questions, "question") : null,
    minutes ? `about ${plural(minutes, "minute")}` : null
  ].filter(Boolean);

  return [
    [client ? `Hi ${client},` : "Hi,", intro].filter(Boolean).join(" "),
    `We've sent you a form${
      title ? ` called ${title}` : ""
    } with the details we need from you.`,
    size.length ? `It's ${size.join(" and takes ")}.` : null,
    uploads
      ? `You'll upload ${plural(uploads, "document")}, so keep them nearby.`
      : null,
    input.due ? `Please finish by ${spokenDate(input.due)}.` : null,
    "You can stop and come back anytime. If anything is unclear, just reply to the email."
  ].filter((line): line is string => Boolean(line));
};

export const linesOf = (text: string): string[] =>
  text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

const wordsIn = (line: string): number =>
  line.split(/\s+/).filter(Boolean).length;

/** Seconds to say `lines` out loud at a relaxed pace. */
export const speakingSeconds = (lines: string[]): number =>
  Math.round(
    lines.reduce((sum, line) => sum + wordsIn(line), 0) / WORDS_PER_SECOND
  );

/** The line being said `elapsed` seconds in; the last line holds at the end. */
export const lineAt = (lines: string[], elapsed: number): number => {
  let end = 0;
  for (let i = 0; i < lines.length; i += 1) {
    end += Math.max(1.5, wordsIn(lines[i]) / WORDS_PER_SECOND);
    if (elapsed < end) return i;
  }
  return Math.max(0, lines.length - 1);
};

/** "0:31", "2:05". */
export const formatClock = (seconds: number): string => {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * MP4 first: it plays on every phone the client might open the link on.
 * Chrome records WebM where it can't do MP4; the backend takes both.
 */
const RECORDING_TYPES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm"
];

export const pickRecordingType = (
  isTypeSupported: (type: string) => boolean
): string | null => RECORDING_TYPES.find(isTypeSupported) ?? null;

export const videoFileName = (type: string): string =>
  type.startsWith("video/mp4") ? "welcome-video.mp4" : "welcome-video.webm";
